import { toast } from '@motorway/mw-highway-code/pre-alpha';

import { T } from 'Features/SellerCommunications/utils/translate';
import { postSellerCommunicationSendEmails } from 'Pages/api/seller-communications/send-emails/client.api';
import {
	patchSellerCommunicationTemplate,
	postSellerCommunicationTemplate,
} from 'Pages/api/seller-communications/template/client.api';
import { SellerCommunicationStatus } from 'Pages/api/seller-communications/types';
import { triggerSellerMessagingSendMessagesButtonClickedEvent } from 'Services/analytics/events';
import type { CreateLabelParams } from 'Services/analytics/events/account/purchases/helpers';

import { SELLER_COMMUNICATION_ERROR_GROUPS, TOAST_DURATION } from './constants';
import { groupFailedResults, resetStoresState } from './helpers';
import { infoService } from './infoService';
import {
	EmailBodyStore,
	EnquiriesSearchStore,
	EnquiriesSelectStore,
	EnquiriesStore,
	ModalFlowStore,
	ProcessingEmailsStore,
} from './stores';
import type { EmailsSendResponse } from './types';
import { ModalSteps } from './types';

const triggerToastOnSendEmails = ({ count, isSuccess }: { count: number; isSuccess: boolean }) => {
	const successTitle = T.translate(
		`createEmail.sendEmailResponse.successful.title.${count === 1 ? 'single' : 'multiple'}`,
		{
			count,
		},
	);
	const successDescription = T.translate('createEmail.sendEmailResponse.successful.description');
	const errorTitle = T.translate('createEmail.sendEmailResponse.failure.title');
	const errorDescription = T.translate(
		`createEmail.sendEmailResponse.failure.description.${count === 1 ? 'single' : 'multiple'}`,
		{ count },
	);

	toast({
		content: isSuccess ? successDescription : errorDescription,
		duration: TOAST_DURATION,
		heading: isSuccess ? successTitle : errorTitle,
		variant: isSuccess ? 'success' : 'error',
	});
};

const resetStateOnModalClose = () => {
	resetStoresState();
	infoService.updateCommunicationInfo();
};

const proceedOnSuccess = () => {
	resetStateOnModalClose();
};

const proceedOnPartialSuccess = (result: EmailsSendResponse) => {
	const { enquiries } = EnquiriesStore.getState();

	const errorGroups = groupFailedResults({
		enquiries,
		errors: result.failedToUpdateSales,
		groups: SELLER_COMMUNICATION_ERROR_GROUPS,
		translatePath: 'processingEmailsError.errorStatus',
	});
	ProcessingEmailsStore.setState({ errorGroups, result });
	ModalFlowStore.setState({ currentStep: ModalSteps.ProcessingEmailsError });
	EnquiriesSearchStore.setState({ filteredSaleIds: {}, keyword: '', noResults: false, search: '' });
	infoService.updateCommunicationInfo();
};

const saveEmailTemplate = async () => {
	const { body, saveAsDefaultBody, templateId } = EmailBodyStore.getState();
	if (saveAsDefaultBody) {
		try {
			if (templateId) {
				await patchSellerCommunicationTemplate({ body, templateId });
			} else {
				await postSellerCommunicationTemplate({ body });
			}
		} catch {
			toast({
				content: T.translate('emailTemplateError.description'),
				heading: T.translate('emailTemplateError.title'),
				variant: 'error',
			});
		}
	}
};

const triggerAnalyticsEvent = (
	{ failedToUpdateEnquiries, updatedEnquiryIds }: CreateLabelParams,
	isSuccess: boolean,
) => {
	triggerSellerMessagingSendMessagesButtonClickedEvent({
		buttonLabel: 'Send messages',
		response: {
			failedToUpdateEnquiries,
			updatedEnquiryIds,
		},
		success: isSuccess,
		url: typeof window !== 'undefined' ? window.location.href : '',
	});
};

const createEmailSubject = () => {
	return `${T.translate('createEmail.emailSubject.dynamicPlaceholder')}`;
};

const sendEmails = async () => {
	const { enquiriesMap } = EnquiriesStore.getState();
	const { selectedSaleIds } = EnquiriesSelectStore.getState();
	const saleIds = selectedSaleIds.map((id) => enquiriesMap[id]?.saleId);

	try {
		const { body } = EmailBodyStore.getState();
		const response = await postSellerCommunicationSendEmails({
			emailBody: body,
			emailSubject: createEmailSubject(),
			saleIds,
		});

		const { failedToUpdateSales, updatedSaleIds } = response;
		const isSuccess = failedToUpdateSales.length === 0;

		triggerAnalyticsEvent(
			{
				failedToUpdateEnquiries: failedToUpdateSales.map((sale) => ({
					...sale,
					enquiryId: enquiriesMap[sale.saleId]?.enquiryId,
					saleId: sale.saleId,
				})),
				updatedEnquiryIds: updatedSaleIds.map((saleId) => enquiriesMap[saleId]?.enquiryId),
			},
			isSuccess,
		);

		if (failedToUpdateSales.length === 0) {
			triggerToastOnSendEmails({ count: updatedSaleIds.length, isSuccess: true });
			proceedOnSuccess();
			return;
		}

		proceedOnPartialSuccess({ failedToUpdateSales, updatedSaleIds });
	} catch {
		resetStateOnModalClose();
		triggerToastOnSendEmails({ count: selectedSaleIds.length, isSuccess: false });
		triggerAnalyticsEvent(
			{
				failedToUpdateEnquiries: selectedSaleIds.map((enquiryId) => ({
					enquiryId,
					saleId: enquiriesMap[enquiryId].saleId,
					status: SellerCommunicationStatus.FAILED_TO_CREATE_MESSAGE,
				})),
				updatedEnquiryIds: [],
			},
			false,
		);
	}
};

const onEmailSend = () => {
	ModalFlowStore.setState({ currentStep: ModalSteps.ProcessingEmails });
	void saveEmailTemplate();
	void sendEmails();
};

export const processingEmailService = {
	onEmailSend,
};
